import { useState, useEffect } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Trans, useTranslation } from "react-i18next";

interface AmountInputProps {
  exchange: {
    id: number;
    symbol: string;
    name: string;
    amount: number;
    type: string;
    currencies?: any;
    minAmount?: string;
    maxAmount?: string;
  };
  setAmount: (amount: number | string) => void;
  handleFocus: (setValue: (param: string) => void, value: string) => void;
  handleBlur: (value: string, setValue: (param: string) => void) => void;
  setIsKeepOrdering: () => void;
  setLimitError: (value: boolean) => void;
  isFieldTouched: boolean;
  setIsFieldTouched: (value: boolean) => void;
  setIsReceiveCalculation: () => void;
}

const AmountInput = ({
  exchange,
  setAmount,
  handleFocus,
  handleBlur,
  setIsKeepOrdering,
  setLimitError,
  isFieldTouched,
  setIsFieldTouched,
  setIsReceiveCalculation,
}: AmountInputProps) => {
  const [inFocus, setInFocus] = useState(false);
  const [internalError, setInternalError] = useState<JSX.Element | null>(null);
  const { i18n } = useTranslation();

  const validateAmount = (value: string) => {
    if (exchange.type !== "CRYPTO") return;

    const numValue = parseFloat(value);

    const min = exchange.minAmount ? parseFloat(exchange.minAmount) : 0;
    const max = exchange.maxAmount ? parseFloat(exchange.maxAmount) : Infinity;

    if (numValue < min) {
      setLimitError(true);
      setInternalError(
        <Trans
          i18nKey={"exchangeError.min"}
          components={{ span: <span /> }}
          values={{ min: min, symbol: exchange.symbol }}
        />
      );
    }
    if (numValue > max) {
      setLimitError(true);
      setInternalError(
        <Trans
          i18nKey={"exchangeError.max"}
          components={{ span: <span /> }}
          values={{ max: max, symbol: exchange.symbol }}
        />
      );
    } else if (numValue > min && numValue < max) {
      setLimitError(false);
      setInternalError(null);
    }
  };

  useEffect(() => {
    if (
      (isFieldTouched || exchange.amount) &&
      exchange.amount !== null &&
      exchange.amount !== undefined
    ) {
      validateAmount(exchange.amount.toString());
    }
  }, [exchange, i18n.language, isFieldTouched]);

  return (
    <>
      <input
        type="text"
        value={inFocus && !exchange.amount ? "" : exchange.amount.toString()}
        onChange={(e) => {
          const inputValue = e.target.value
            .replace(/,/g, ".")
            .replace(/^0*(\d+)/, "$1");

          if (inputValue === ".") {
            setAmount("0.");
          } else if (
            /^\d+(\.\d{0,6})?$/.test(inputValue) ||
            inputValue === ""
          ) {
            setAmount(inputValue);
          }
          setIsKeepOrdering();
          setIsFieldTouched(true);
          setIsReceiveCalculation();
        }}
        step="any"
        onFocus={(e) => {
          setInFocus(true);
          handleFocus((value: string) => setAmount(value), e.target.value);
        }}
        onBlur={(e) => {
          setInFocus(false);
          handleBlur(e.target.value, (value: string) => setAmount(value));
        }}
      />

      {internalError && exchange.type == "CRYPTO" && (
        <div className="limit-error-message">
          <InfoOutlinedIcon /> {internalError}
        </div>
      )}
    </>
  );
};

export default AmountInput;
